import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Download } from "lucide-react";
import { toast } from '@/hooks/use-toast';
import { fetchDiscountAnalysisHistory, fetchDiscountAnalysisById } from '@/services/api';
import DiscountDetailedView from './DiscountDetailedView';

interface HistoryItem {
  discountAnalysisId: string;
  vendorName: string;
  created_at: string;
  status: string;
  progress: string;
  analysisReport?: string;
}

const DiscountHistoryTab = () => {
  const [historyData, setHistoryData] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailedViewData, setDetailedViewData] = useState<any>(null);
  const [showDetailedView, setShowDetailedView] = useState(false);

  useEffect(() => {
    fetchHistoryData();
  }, []);

  const fetchHistoryData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchDiscountAnalysisHistory();
      setHistoryData(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching history data:', err);
      setError('Failed to load history data');
      toast({
        title: "Error",
        description: "Failed to load discount analysis history",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewClick = async (discountAnalysisId: string) => {
    try {
      const detailData = await fetchDiscountAnalysisById(discountAnalysisId);
      console.log('Detailed view data:', detailData);
      setDetailedViewData(detailData);
      setShowDetailedView(true);
      // Refresh the history data to show updated information
      await fetchHistoryData();
      toast({
        title: "Success",
        description: "Analysis details loaded successfully",
      });
    } catch (err) {
      console.error('Error fetching analysis details:', err);
      toast({
        title: "Error",
        description: "Failed to load analysis details",
        variant: "destructive",
      });
    }
  };

  const handleCloseDetailedView = () => {
    setShowDetailedView(false);
    setDetailedViewData(null);
  };

  const handleReportDownload = (reportUrl: string, analysisId: string) => {
    if (reportUrl) {
      const link = document.createElement('a');
      link.href = reportUrl;
      link.download = `discount-analysis-${analysisId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      toast({
        title: "Error",
        description: "Report not available for download",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      'completed': 'default',
      'processing': 'secondary',
      'failed': 'destructive',
      'pending': 'outline'
    } as const;
    
    const variant = variants[status?.toLowerCase() as keyof typeof variants] || 'secondary';
    
    return (
      <Badge variant={variant}>
        {status}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (showDetailedView && detailedViewData) {
    return (
      <DiscountDetailedView 
        data={detailedViewData} 
        onClose={handleCloseDetailedView}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-500">Loading history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Discount Analysis History</h3>
        <Button variant="outline" onClick={fetchHistoryData}>
          Refresh
        </Button>
      </div>
      
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Analysis ID</TableHead>
              <TableHead>Vendor Name</TableHead>
              <TableHead>Created Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {historyData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                  No discount analysis history found
                </TableCell>
              </TableRow>
            ) : (
              historyData.map((item) => (
                <TableRow key={item.discountAnalysisId}>
                  <TableCell className="font-medium">{item.discountAnalysisId}</TableCell>
                  <TableCell>{item.vendorName}</TableCell>
                  <TableCell>{formatDate(item.created_at)}</TableCell>
                  <TableCell>{getStatusBadge(item.status)}</TableCell>
                  <TableCell>{item.progress || '-'}</TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleViewClick(item.discountAnalysisId)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {item.analysisReport && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleReportDownload(item.analysisReport!, item.discountAnalysisId)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default DiscountHistoryTab;
